import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Plus, MessageSquare, AlertCircle, TrendingUp, Clock, ShieldQuestion } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import FeedbackConfirmationDialog from '@/components/product-feedback/FeedbackConfirmationDialog';
import FeedbackForm from '@/components/product-feedback/FeedbackForm';
import FeedbackTable from '@/components/product-feedback/FeedbackTable';
import FeedbackDetail from '@/components/product-feedback/FeedbackDetail';
import { useProductFeedback } from '@/hooks/useProductFeedback';
import { useUserPermissions } from '@/hooks/useUserPermissions';
import { TabGroup } from '@/components/common/TabGroup';
import { SearchBar } from '@/components/common/SearchBar';

export default function ProductFeedback() {
  const { t } = useTranslation();
  const { feedbackItems, isLoading } = useProductFeedback();
  const { isAdmin } = useUserPermissions();

  // UI State
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [activeTypeTab, setActiveTypeTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    status: 'all',
    module: 'all',
    priority: 'all',
    approval: 'all',
  });

  // Extract unique modules
  const uniqueModules = useMemo(() => {
    if (!feedbackItems) return [];
    return [...new Set(feedbackItems.map(item => item.module))].sort();
  }, [feedbackItems]);

  // Calculate statistics
  const statistics = useMemo(() => {
    if (!feedbackItems) return { total: 0, open: 0, critical: 0, inProgress: 0, pendingApproval: 0 };
    
    return {
      total: feedbackItems.length,
      open: feedbackItems.filter(item => item.status === 'open').length,
      critical: feedbackItems.filter(item => item.priority === 'critical' && item.status !== 'closed').length,
      inProgress: feedbackItems.filter(item => item.status === 'in_progress').length,
      pendingApproval: feedbackItems.filter(item => item.approval_status === 'pending_approval').length,
    };
  }, [feedbackItems]);

  // Count active items by type for tabs (open + in_progress, excluding rejected requests)
  const typeCounts = useMemo(() => {
    if (!feedbackItems) return { all: 0, bug: 0, requests: 0 };
    
    const isActiveStatus = (status) => status === 'open' || status === 'in_progress';
    
    const activeBugs = feedbackItems.filter(item => 
      isActiveStatus(item.status) && item.type === 'bug'
    );
    
    const activeRequests = feedbackItems.filter(item => 
      isActiveStatus(item.status) && 
      (item.type === 'feature' || item.type === 'improvement') &&
      item.approval_status !== 'rejected'
    );
    
    return {
      all: activeBugs.length + activeRequests.length,
      bug: activeBugs.length,
      requests: activeRequests.length,
    };
  }, [feedbackItems]);

  // Filter feedback
  const filteredFeedback = useMemo(() => {
    if (!feedbackItems) return [];
    
    // Definir ordem de prioridade dos status
    const statusOrder = {
      'in_progress': 1,
      'open': 2,
      'resolved': 3,
      'closed': 4,
    };
    
    return feedbackItems.filter(item => {
      // Type filter (tab-based): 'requests' = feature + improvement
      if (activeTypeTab === 'bug' && item.type !== 'bug') return false;
      if (activeTypeTab === 'requests' && item.type !== 'feature' && item.type !== 'improvement') return false;
      
      // Status filter
      if (filters.status !== 'all' && item.status !== filters.status) return false;
      
      // Module filter
      if (filters.module !== 'all' && item.module !== filters.module) return false;
      
      // Priority filter
      if (filters.priority !== 'all' && item.priority !== filters.priority) return false;

      // Approval filter
      if (filters.approval !== 'all') {
        if (filters.approval === 'pending_approval' && item.approval_status !== 'pending_approval') return false;
        if (filters.approval === 'approved' && item.approval_status !== 'approved') return false;
        if (filters.approval === 'rejected' && item.approval_status !== 'rejected') return false;
      }
      
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          item.title.toLowerCase().includes(query) ||
          item.description.toLowerCase().includes(query) ||
          item.module.toLowerCase().includes(query)
        );
      }
      
      return true;
    }).sort((a, b) => {
      // Ordenar por status: In Progress → Open → Resolved → Closed
      const statusOrder = {
        'in_progress': 1,
        'open': 2,
        'resolved': 3,
        'closed': 4,
      };
      const orderA = statusOrder[a.status] || 99;
      const orderB = statusOrder[b.status] || 99;
      return orderA - orderB;
    });
  }, [feedbackItems, activeTypeTab, filters, searchQuery]);

  const handleSubmitFeedbackClick = () => {
    setShowConfirmation(true);
  };

  const handleConfirmationAccept = () => {
    setShowConfirmation(false);
    setShowForm(true);
  };

  const handleViewFeedback = (feedback) => {
    setSelectedFeedback(feedback);
  };

  return (
    <div className="container mx-auto px-4 md:px-6 py-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
            <MessageSquare className="h-8 w-8 text-primary" />
            {t('productFeedback.title')}
          </h1>
          <p className="text-muted-foreground mt-1">
            {t('productFeedback.subtitle')}
          </p>
        </div>
        <Button onClick={handleSubmitFeedbackClick} size="lg">
          <Plus className="h-5 w-5 mr-2" />
          {t('productFeedback.submitFeedback')}
        </Button>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('productFeedback.stats.totalRequests')}</CardTitle>
            <MessageSquare className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{statistics.total}</div>
            <p className="text-xs text-muted-foreground">{t('productFeedback.stats.allFeedbackItems')}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('productFeedback.stats.openItems')}</CardTitle>
            <AlertCircle className="h-4 w-4 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-500">{statistics.open}</div>
            <p className="text-xs text-muted-foreground">{t('productFeedback.stats.awaitingReview')}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('productFeedback.stats.criticalPriority')}</CardTitle>
            <AlertCircle className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-500">{statistics.critical}</div>
            <p className="text-xs text-muted-foreground">{t('productFeedback.stats.requiresAttention')}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('productFeedback.stats.inProgress')}</CardTitle>
            <Clock className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-500">{statistics.inProgress}</div>
            <p className="text-xs text-muted-foreground">{t('productFeedback.stats.beingWorkedOn')}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('productFeedback.stats.pendingApproval')}</CardTitle>
            <ShieldQuestion className="h-4 w-4 text-amber-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-500">{statistics.pendingApproval}</div>
            <p className="text-xs text-muted-foreground">{t('productFeedback.stats.awaitingAdminApproval')}</p>
          </CardContent>
        </Card>
      </div>

      {/* Type Tabs */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <TabGroup
          tabs={[
            { id: 'all', label: t('productFeedback.tabs.all'), badge: typeCounts.all },
            { id: 'bug', label: `🐛 ${t('productFeedback.tabs.bugs')}`, badge: typeCounts.bug },
            { id: 'requests', label: `✨ ${t('productFeedback.tabs.requests')}`, badge: typeCounts.requests },
          ]}
          activeTab={activeTypeTab}
          onTabChange={setActiveTypeTab}
        />
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col lg:flex-row gap-4">
        <div className="flex-1">
          <SearchBar
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('productFeedback.filters.searchPlaceholder')}
            className="w-full"
          />
        </div>
        
        <div className="flex flex-wrap gap-2">
          <Select value={filters.status} onValueChange={(value) => setFilters({...filters, status: value})}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('productFeedback.filters.allStatus')}</SelectItem>
              <SelectItem value="open">{t('productFeedback.status.open')}</SelectItem>
              <SelectItem value="in_progress">{t('productFeedback.status.in_progress')}</SelectItem>
              <SelectItem value="resolved">{t('productFeedback.status.resolved')}</SelectItem>
              <SelectItem value="closed">{t('productFeedback.status.closed')}</SelectItem>
            </SelectContent>
          </Select>

          <Select value={filters.module} onValueChange={(value) => setFilters({...filters, module: value})}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder={t('productFeedback.table.module')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('productFeedback.filters.allModules')}</SelectItem>
              {uniqueModules.map(module => (
                <SelectItem key={module} value={module}>{module}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={filters.priority} onValueChange={(value) => setFilters({...filters, priority: value})}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder={t('productFeedback.table.priority')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('productFeedback.filters.allPriority')}</SelectItem>
              <SelectItem value="low">🟢 {t('productFeedback.priority.low')}</SelectItem>
              <SelectItem value="medium">🟡 {t('productFeedback.priority.medium')}</SelectItem>
              <SelectItem value="high">🟠 {t('productFeedback.priority.high')}</SelectItem>
              <SelectItem value="critical">🔴 {t('productFeedback.priority.critical')}</SelectItem>
            </SelectContent>
          </Select>

          <Select value={filters.approval} onValueChange={(value) => setFilters({...filters, approval: value})}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder={t('productFeedback.table.approval')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('productFeedback.filters.allApproval')}</SelectItem>
              <SelectItem value="pending_approval">⏳ {t('productFeedback.approvalStatus.pending_approval')}</SelectItem>
              <SelectItem value="approved">✅ {t('productFeedback.approvalStatus.approved')}</SelectItem>
              <SelectItem value="rejected">❌ {t('productFeedback.approvalStatus.rejected')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Feedback Table */}
      <FeedbackTable
        feedbackItems={filteredFeedback}
        isLoading={isLoading}
        onViewFeedback={handleViewFeedback}
      />

      {/* Modals */}
      <FeedbackConfirmationDialog
        open={showConfirmation}
        onConfirm={handleConfirmationAccept}
        onCancel={() => setShowConfirmation(false)}
      />

      <FeedbackForm
        open={showForm}
        onClose={() => setShowForm(false)}
      />

      {selectedFeedback && (
        <FeedbackDetail
          feedback={selectedFeedback}
          open={!!selectedFeedback}
          onClose={() => setSelectedFeedback(null)}
          isAdmin={isAdmin}
        />
      )}
    </div>
  );
}
